import React, { useState, useEffect } from 'react';
import dynamicFormService from '../../service/dynamicForm.service';
import {
  InputUI,
  SelectUI,
  DatePickerUI,
  AutoCompleteSelectUI,
  CheckboxGroupUI,
  DialogUI,
  ButtonUI,
} from '../Interface';
import ContactSignature from './ContactSignature';

import masterDataService from '../../service/masterData.service';
import { Box, Container, Grid, Typography } from '@mui/material';
import companyService from '../../service/company.service';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import {
  getDynamicFormAutocompleteValue,
  getDynamicFormFieldOptions,
  getDynamicFormInitialValues,
} from '../../utils/dynamicFormHelper';
import { useLoader } from '../../provider/LoaderProvider';

const DynamicFormFiller = () => {
  const { showLoader, hideLoader } = useLoader();
  const [formList, setFormList] = useState([]);
  const [formsData, setFormsData] = useState([]);
  const [selectedForm, setSelectedForm] = useState(null);
  const [fields, setFields] = useState([]);
  const [formValues, setValues] = useState({});
  const [projectsList, setProjectsList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [project, setProject] = useState(null);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [contactSignatures, setContactSignatures] = useState({});
  const [dynamicFormHeadId, setDynamicFormHeadId] = useState(null);

  // Képviselők kiválasztása és aláírás
  const [repDialogOpen, setRepDialogOpen] = useState(false);
  const [representatives, setRepresentatives] = useState([]);
  const [selectedRepresentative, setSelectedRepresentative] = useState([]);
  const [representativeSignatures, setRepresentativeSignatures] = useState({});

  // Contactok kiválasztása dialógusban
  const handleOpenContactDialog = () => setContactDialogOpen(true);
  const handleCloseContactDialog = () => setContactDialogOpen(false);
  const handleContactChange = (checkedIds) => {
    setSelectedContacts(checkedIds);
  };

  // Egy contact aláírásának mentése
  const handleSignatureChange = (contactId, dataUrl) => {
    setContactSignatures((prev) => ({ ...prev, [contactId]: dataUrl }));
  };

  // Képviselő dialógus kezelők
  const handleOpenRepDialog = () => setRepDialogOpen(true);
  const handleCloseRepDialog = () => setRepDialogOpen(false);
  const handleRepresentativeChange = (selectedId) => {
    setSelectedRepresentative(selectedId);
  };
  const handleRepSignatureChange = (repId, dataUrl) => {
    setRepresentativeSignatures((prev) => ({ ...prev, [repId]: dataUrl }));
  };

  useEffect(() => {
    masterDataService.getDynamicFroms().then((data) => {
      setFormList(
        data.map((form) => ({
          label: form.name,
          value: form.id,
        }))
      );
      setFormsData(data);
    });

    masterDataService.projectToForm().then((data) => {
      setProjectsList(
        data
          .filter((project) => project.active)
          .map((project) => ({
            label: project.projectName,
            value: project.projectId,
          }))
      );
      setProjects(data);
    });

    // Képviselő lista betöltése (csak az aktív értékesítők)
    masterDataService.usersToForm().then((data) => {
      const reps = (data || [])
        .filter((p) => (p?.active ?? true) && (p?.salesPerson ?? true))
        .map((p) => ({ label: p.nickName, value: p.userId }));
      setRepresentatives(reps);
    });
  }, []);

  useEffect(() => {
    if (project?.projectId) {
      if (!project.companyId) {
        setContacts([]);
        setSelectedContacts([]);
        addNotification({
          content: 'A kiválasztott projekt nem rendelkezik partnerrel!',
          type: NotificationType.WARNING,
        });
        return;
      }

      companyService.getCompanyById(project.companyId).then((data) => {
        setContacts(
          (data?.companyContact || []).map((contact) => ({
            label: contact.name,
            value: contact.id,
          }))
        );
      });
    } else {
      setContacts([]);
      setSelectedRepresentative([]);
    }
  }, [project]);

  useEffect(() => {
    if (selectedForm?.id) {
      dynamicFormService.getItem(selectedForm.id).then((data) => {
        const dynamicFormFields = (data?.dynamicFormField || []).filter(
          (field) => !field.systemRole && !field.onlyWithoutProject
        );
        setFields(dynamicFormFields);
        setValues(getDynamicFormInitialValues(dynamicFormFields));
      });
    } else {
      setFields([]);
      setValues({});
    }
  }, [selectedForm]);

  const setFieldValue = (fieldId, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [fieldId]: value,
    }));
  };

  const renderField = (field) => {
    switch (field.type) {
      case 'TEXT':
      case 'LONG_TEXT':
        return (
          <InputUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            rows={field.type === 'LONG_TEXT' ? 3 : 1}
            variant="standard"
          />
        );
      case 'DECIMAL':
        return (
          <InputUI
            label={field.name}
            required={field.required}
            type="number"
            value={formValues?.[field.id] ?? ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            variant="standard"
          />
        );
      case 'COMBO':
        if (field.autocomplete || field.multiselect) {
          return (
            <AutoCompleteSelectUI
              label={field.name}
              required={field.required}
              options={getDynamicFormFieldOptions(field)}
              value={getDynamicFormAutocompleteValue(field, formValues?.[field.id])}
              multiple={field.multiselect}
              onChange={(_e, newValue) =>
                setFieldValue(
                  field.id,
                  field.multiselect ? (newValue || []).map((option) => option.value) : newValue?.value ?? null
                )
              }
              variant="standard"
            />
          );
        }
        return (
          <SelectUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] ?? ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            option={getDynamicFormFieldOptions(field)}
            variant="standard"
          />
        );
      case 'DATE':
        return (
          <DatePickerUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] || null}
            onChange={(val) => setFieldValue(field.id, val)}
            variant="standard"
          />
        );
      case 'STATIC':
        return <Typography style={{ padding: '8px 0', fontStyle: 'italic' }}>{field.staticText}</Typography>;
      default:
        return (
          <InputUI
            label={field.name}
            required={field.required}
            value={formValues?.[field.id] || ''}
            onChange={(e) => setFieldValue(field.id, e.target.value)}
            variant="standard"
          />
        );
    }
  };

  // Mentés
  const handleSave = async (preview = false) => {
    const missingRequiredField = fields.find((field) => {
      if (!field.required || field.type === 'STATIC') return false;

      const value = formValues?.[field.id];
      return (
        value === undefined ||
        value === null ||
        (typeof value === 'string' && value.trim() === '') ||
        (Array.isArray(value) && value.length === 0)
      );
    });

    if (missingRequiredField) {
      addNotification({
        content: `A(z) ${missingRequiredField.name} mező kitöltése kötelező!`,
        type: NotificationType.WARNING,
      });
      return;
    }

    const payload = {
      formId: selectedForm?.id,
      projectId: project?.projectId,
      values: formValues,
      signatures: contactSignatures,
      representativeSignatures: representativeSignatures,
      dynamicFormHeadId,
      isPreview: preview,
    };
    showLoader();
    dynamicFormService
      .addForm(payload)
      .then((data) => {
        addNotification({ content: 'Sikeres mentés!', type: NotificationType.SUCCESS });
        // Reset állapotok
        if (!preview) {
          setValues({});
          setSelectedForm(null);
          setProject(null);
          setSelectedContacts([]);
          setContactSignatures({});
          setSelectedRepresentative([]);
          setRepresentativeSignatures({});
          setFields([]);
          setDynamicFormHeadId(null);
          return;
        }

        addNotification({ content: 'Előnézet elkészült!', type: NotificationType.INFO });
        setDynamicFormHeadId(data.headId);

        if (data?.templatePath) {
          const url = `${process.env.REACT_APP_API_URL}/${data.templatePath}`;
          window.open(url, '_blank');
        }
      })
      .finally(() => {
        hideLoader();
      });
  };

  return (
    <>
      <Container maxWidth="md">
        <Grid mt={2} container>
          <Grid item xs={12} mb={2}>
            <AutoCompleteSelectUI
              label="Projekt kiválasztása"
              options={projectsList}
              value={projectsList.find((p) => p.value === project?.projectId) || null}
              onChange={(_e, newVal) =>
                setProject(newVal?.value ? projects.find((proj) => proj.projectId === newVal.value) : null)
              }
              placeholder="Válassz projektet"
            />
          </Grid>

          <Grid item xs={12} mb={2}>
            <AutoCompleteSelectUI
              label="Űrlap kiválasztása"
              options={formList}
              value={formList.find((form) => form.value === selectedForm?.id) || null}
              onChange={(_e, newVal) =>
                setSelectedForm(newVal?.value ? formsData.find((form) => form.id === newVal?.value) : null)
              }
              placeholder="Válassz egy űrlapot"
            />
          </Grid>

          <Grid item xs={6} mt={2}>
            <ButtonUI
              text="Kapcsolattartók kiválasztása"
              color="blue-gray"
              variant="gradient"
              onClick={handleOpenContactDialog}
              disabled={!project?.projectId}
            />
            {selectedContacts.length > 0 && (
              <div className="mt-2 text-sm text-gray-700">
                Kiválasztva:{' '}
                {contacts
                  .filter((c) => selectedContacts.includes(c.value.toString()))
                  .map((c) => c.label)
                  .join(', ')}
              </div>
            )}
          </Grid>
          <Grid item xs={6} mt={2}>
            <ButtonUI
              text="Képviselő kiválasztása"
              color="blue-gray"
              variant="gradient"
              onClick={handleOpenRepDialog}
              disabled={!project?.projectId}
            />
            {selectedRepresentative.length > 0 && (
              <div className="mt-2 text-sm text-gray-700">
                Kiválasztva:{' '}
                {representatives
                  .filter((r) => selectedRepresentative.includes(String(r.value)))
                  ?.map((r) => r.label)
                  .join(', ')}
              </div>
            )}
          </Grid>

          {fields.length > 0 && (
            <>
              {fields.map((field) => (
                <Grid item xs={12} mt={1} key={field.id}>
                  {renderField(field)}
                </Grid>
              ))}
            </>
          )}
        </Grid>

        {/* Kapcsolattartók aláírásai a form után */}
        {selectedContacts.length > 0 && (
          <Box mt={6}>
            <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Kapcsolattartók aláírásai</h3>
            {contacts
              .filter((c) => selectedContacts.includes(c.value.toString()))
              .map((contact) => (
                <ContactSignature
                  key={contact.value}
                  contact={contact}
                  value={contactSignatures[contact.value]}
                  onChange={handleSignatureChange}
                />
              ))}
          </Box>
        )}

        {/* Képviselő aláírása */}
        {selectedRepresentative.length > 0 && (
          <Box mt={6}>
            <h3 style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Képviselő aláírása</h3>
            {representatives
              .filter((r) => selectedRepresentative.includes(r.value.toString()))
              .map((rep) => (
                <ContactSignature
                  key={rep.value}
                  contact={rep}
                  value={contactSignatures[rep.value]}
                  onChange={handleRepSignatureChange}
                />
              ))}
          </Box>
        )}

        {/* Mentés gomb */}
        <Box mt={6} display="flex" justifyContent="center">
          <ButtonUI
            text="Mentés"
            color="green"
            variant="gradient"
            onClick={() => handleSave(false)}
            disabled={!selectedForm || !project}
          />
          <ButtonUI
            style={{ marginLeft: '2rem' }}
            text="Előnézet"
            color="blue"
            variant="gradient"
            onClick={() => handleSave(true)}
            disabled={!selectedForm || !project}
          />
        </Box>
      </Container>
      {/* Contact kiválasztó dialógus */}
      <DialogUI
        open={contactDialogOpen}
        onClose={handleCloseContactDialog}
        headerContent="Kapcsolattartók kiválasztása"
        fullWidth={true}
        maxWidth="md"
      >
        <CheckboxGroupUI options={contacts} value={selectedContacts} onChange={handleContactChange} mode="multi" />
        <Box display="flex" justifyContent="center" mt={4}>
          <ButtonUI text="Kész" onClick={handleCloseContactDialog} className="bg-success" />
        </Box>
      </DialogUI>

      {/* Képviselő kiválasztó dialógus */}
      <DialogUI
        open={repDialogOpen}
        onClose={handleCloseRepDialog}
        headerContent="Képviselő kiválasztása"
        fullWidth={true}
        maxWidth="md"
      >
        <CheckboxGroupUI
          options={representatives}
          value={selectedRepresentative}
          onChange={handleRepresentativeChange}
          mode="multi"
        />
        <Box display="flex" justifyContent="center" mt={4}>
          <ButtonUI text="Kész" onClick={handleCloseRepDialog} className="bg-success" />
        </Box>
      </DialogUI>
    </>
  );
};

export default DynamicFormFiller;
